import { useContext, useEffect, useState } from "react";
import { api } from "../../api";
import { CustomerContext } from "../../providers";
import { Button } from "../tailwind/button";
import { Field, Fieldset, Legend } from "../tailwind/fieldset";
import { Text } from "../tailwind/text";

export const SychronizeContacts = () => {
  const { customer, shop } = useContext(CustomerContext);

  const [isConnectKlaviyoLoading, setIsConnectKlaviyoLoading] = useState(false);
  const [isConnectShopifyLoading, setIsConnectShopifyLoading] = useState(false);
  const [shopBillingStatus, setShopBillingStatus] = useState(null);

  const titleClasses = "font-bold";
  const fieldClasses = "mb-2";

  const handleConnectKlaviyo = async () => {
    setIsConnectKlaviyoLoading(true);
    try {
      const installationUrl = await api.generateKlaviyoInstallationUrl({
        customerId: customer.id,
      });
      window.open(installationUrl, "_blank");
    } catch (error) {
      console.error("Error connecting to Klaviyo:", error);
    } finally {
      setIsConnectKlaviyoLoading(false);
    }
  };
  const handleDisconnectKlaviyo = async () => {
    setIsConnectKlaviyoLoading(true);

    try {
      await api.klaviyo.disconnect();
    } catch (e) {
      console.error(e);
    } finally {
      setIsConnectKlaviyoLoading(false);
      window.location.href =
        "/settings/data/integrations/klaviyo/connexion?disconnected=true";
    }
  };

  // Check billing status when shop is available
  useEffect(() => {
    const checkBillingStatus = async () => {
      if (shop?.id) {
        try {
          const response = await fetch(`/api/billing/status?shopId=${shop.id}`);
          if (response.ok) {
            const status = await response.json();
            setShopBillingStatus(status);
          }
        } catch (error) {
          console.error("Error checking billing status:", error);
        }
      }
    };

    checkBillingStatus();
  }, [shop?.id]);

  const handleConnectShopify = async () => {
    // If shop is already connected, enable subscription
    if (shop?.id) {
      setIsConnectShopifyLoading(true);
      try {
        // Call the enableSubscription action on the shopifyShop model
        const result = await api.shopifyShop.enableSubscription(shop.id);

        if (result.error) {
          console.error("Billing error:", result.error);
          alert(`Failed to activate billing: ${result.error.message}`);
          return;
        }

        // Wait 1 second to ensure data is persisted
        await new Promise((resolve) => setTimeout(resolve, 2000));

        // Fetch the updated shop record to get confirmationUrl
        const updatedShop = await api.shopifyShop.findOne(shop.id, {
          select: {
            id: true,
            confirmationUrl: true,
          },
        });

        if (!updatedShop) {
          alert("Failed to fetch shop data. Please try again.");
          return;
        }

        const confirmationUrl = updatedShop.confirmationUrl;

        if (!confirmationUrl) {
          console.error("No confirmation URL in shop:", updatedShop);
          alert("No confirmation URL received. Please try again.");
          return;
        }

        // Redirect to Shopify billing confirmation
        window.location.assign(confirmationUrl);
      } catch (error) {
        console.error("Error starting billing:", error);
        const errorMessage =
          error.message || "Failed to activate billing. Please try again.";
        alert(errorMessage);
      } finally {
        setIsConnectShopifyLoading(false);
      }
    } else {
      // No shop connected yet, go to Shopify app store
      window.open("https://apps.shopify.com/simio", "_blank");
    }
  };

  return (
    <div className="">
      <Fieldset>
        <Legend className="mb-10">
          By importing your contacts into Simio, you certify that each person
          has given their consent to be contacted on WhatsApp.
        </Legend>
        <Field className={fieldClasses}>
          <Text className={titleClasses}>Shopify</Text>
          {shop ? (
            <>
              <Text>Your account is linked to {shop.name}'s store</Text>
              {shopBillingStatus && !shopBillingStatus.planActive && (
                <Button
                  color="secondaryButton"
                  loading={isConnectShopifyLoading}
                  onClick={handleConnectShopify}
                  className="mt-2"
                >
                  Activate Billing ({shopBillingStatus.planName})
                </Button>
              )}
              {shopBillingStatus?.planActive && (
                <Text className="text-green-600 mt-2">
                  ✓ Billing active: {shopBillingStatus.planName}
                </Text>
              )}
            </>
          ) : (
            <Button
              color="secondaryButton"
              onClick={handleConnectShopify}
              loading={isConnectShopifyLoading}
            >
              Connect Shopify
            </Button>
          )}
        </Field>
        <Field className={fieldClasses}>
          <Text className={titleClasses}>Klaviyo</Text>
          {customer.klaviyoAccessToken ? (
            <>
              <Text>Your account is linked to Klaviyo</Text>
              <Button
                color="secondaryButton"
                loading={isConnectKlaviyoLoading}
                onClick={handleDisconnectKlaviyo}
              >
                Disconnect Klaviyo
              </Button>
            </>
          ) : (
            <Button
              color="secondaryButton"
              loading={isConnectKlaviyoLoading}
              onClick={handleConnectKlaviyo}
            >
              Connect Klaviyo
            </Button>
          )}
        </Field>
        <Field className={fieldClasses}>
          <Text className={titleClasses}>CSV imports</Text>
          <Text>
            If you don't have Shopify or Klaviyo, you can still import your
            contacts with csv files.
          </Text>
        </Field>
      </Fieldset>
    </div>
  );
};
